/**
 * GenerateFixModal.jsx
 * ------------------------------------------------------------------
 * Modal that fetches an AI-generated, copy-paste ready fix for ONE
 * specific issue on ONE specific URL inside a scan report. Used by
 * `EshopScanReport` per-row "✨ AI Fix" buttons.
 *
 * Flow:
 *   1. Mount with `{reportId, row, issue}` → POST /api/eshop-seo/fix
 *   2. Show loading spinner ~3-6s (GPT-4o-mini)
 *   3. Render the {category, fix_code, explanation} card with a
 *      one-click copy-to-clipboard button.
 *   4. Backend caches the fix in `eshop_seo_fixes`, so re-opening the
 *      same row instantly re-renders (no LLM burn).
 *
 * Keep the modal lean — no dependencies on shadcn `<Dialog>` (which is
 * portal-based and conflicts with the report table's z-index). Plain
 * fixed-overlay + ESC handler is enough.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { X, Copy, Check, Loader2, Sparkles, AlertTriangle } from "lucide-react";

export default function GenerateFixModal({
  open, onClose, reportId, row, issue, userEmail,
}) {
  const [loading, setLoading] = useState(false);
  const [fix, setFix] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Fetch the fix every time the modal opens with a fresh (row, issue).
  // Cached server-side, so repeats are cheap and instant.
  useEffect(() => {
    if (!open || !reportId || !row || !issue) return;
    let cancelled = false;
    async function run() {
      setLoading(true); setError(""); setFix(null); setCopied(false);
      try {
        const r = await axios.post(`${API}/eshop-seo/fix`, {
          report_id: reportId,
          url: row.url,
          issue,
          page_type: row.type || "page",
          title: row.title || "",
          meta_description: row.meta_description || "",
          h1: row.h1 || "",
          language: "sk",
        }, { headers: { "X-User-Email": userEmail } });
        if (!cancelled) setFix(r.data?.fix || null);
      } catch (e) {
        if (!cancelled) setError(
          e?.response?.data?.detail || "Fix sa nepodarilo vygenerovať. Skús znova."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [open, reportId, row, issue, userEmail]);

  // Close on ESC key — friendly UX, also catches accidental focus traps.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = () => {
    if (!fix?.fix_code) return;
    navigator.clipboard.writeText(fix.fix_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="eshop-fix-modal"
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-950 border border-purple-700/40 rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-900/40 to-gray-950 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-400">AI Fix</div>
              <div className="text-sm font-bold text-white truncate max-w-[420px]">{issue}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-sm"
            data-testid="eshop-fix-modal-close"
            aria-label="Zavrieť"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 py-5 space-y-4">
          <div className="text-xs text-gray-400 break-all" data-testid="eshop-fix-modal-url">
            <span className="text-gray-500 mr-2">URL:</span>
            <a
              href={row.url} target="_blank" rel="noreferrer"
              className="text-purple-400 hover:underline"
            >{row.url}</a>
          </div>

          {loading && (
            <div className="flex items-center gap-3 p-6 bg-gray-900/50 border border-gray-800 rounded-sm" data-testid="eshop-fix-loading">
              <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
              <div>
                <div className="text-sm font-bold">Generujem fix cez GPT-4o…</div>
                <div className="text-xs text-gray-400 mt-1">Zvyčajne to trvá 3–6 sekúnd.</div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 p-4 bg-rose-900/30 border border-rose-700/40 rounded-sm" data-testid="eshop-fix-error">
              <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5" />
              <div className="text-sm text-rose-200">{error}</div>
            </div>
          )}

          {fix && (
            <div className="space-y-3" data-testid="eshop-fix-result">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-purple-900/40 text-purple-300 border border-purple-700/40 rounded-sm">
                  {fix.category}
                </span>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-gray-900 text-gray-400 border border-gray-700 rounded-sm">
                  {row.type || "page"}
                </span>
              </div>

              {/* Copy-paste fix code block */}
              <div className="relative">
                <pre className="p-4 bg-black/70 border border-gray-800 rounded-sm text-xs text-emerald-300 font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="eshop-fix-code">
                  {fix.fix_code}
                </pre>
                <button
                  onClick={handleCopy}
                  data-testid="eshop-fix-copy-btn"
                  className="absolute top-2 right-2 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-500 text-white rounded-sm flex items-center gap-1"
                >
                  {copied
                    ? (<><Check className="w-3 h-3" /> Skopírované</>)
                    : (<><Copy className="w-3 h-3" /> Kopírovať</>)
                  }
                </button>
              </div>

              {fix.explanation && (
                <div className="p-3 bg-gray-900/50 border border-gray-800 rounded-sm">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Prečo to funguje</div>
                  <div className="text-sm text-gray-300" data-testid="eshop-fix-explanation">
                    {fix.explanation}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
